import { Box3, Vector3 } from "three";
import { Ioc } from "@core/ioc/ioc";
import { PICKING_SERVICE, PickingService } from "@runtime/picking/pickingService";
import { ORBIT_CAMERA, type OrbitCameraController } from "./orbitCameraController";

/**
 * Frame the camera on a set of snapshots — the web equivalent of Unity's
 * `ISceneInputModel.OnFocusAction(excludedTags: [CampfireTags.Origin])`, which
 * every Focus entry point funnels through (the hierarchy / viewport context
 * menu, the `F` shortcut, and the AI `Focus_On_Part` tool).
 *
 * Lives here rather than in a caller because all three want the same framing
 * and the same tolerance for a missing viewport, and because the origin
 * exclusion is a property of focus, not of whoever asked for it: the origin
 * marker carries no geometry, so it contributes nothing to the union box and
 * an origin-only request correctly reports `"no-bounds"` instead of pointing
 * the camera at a null volume.
 *
 * Non-blocking on purpose: `focus` glides (1 s ease-out) and callers return
 * immediately, matching Unity's `sceneComponent.Focus`.
 */
export type FocusResult = "focused" | "camera-unavailable" | "no-bounds";

/** Bounds beyond this are renderer pollution, not geometry (`sanitizeBbox`). */
const K_POLLUTION_THRESHOLD = 9000;

export function focusSnapshots(snapshotIds: ReadonlyArray<string>): FocusResult {
  if (snapshotIds.length === 0) return "no-bounds";
  if (
    !Ioc.isRegistered<PickingService>(PICKING_SERVICE)
    || !Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)
  ) {
    return "camera-unavailable";
  }

  const picker = Ioc.resolve<PickingService>(PICKING_SERVICE);
  const bbox = new Box3();
  let hasBounds = false;
  for (const snapshotId of snapshotIds) {
    const node = picker.resolveSnapshotNode(snapshotId);
    if (!node) continue;
    const nodeBox = new Box3().setFromObject(node);
    if (nodeBox.isEmpty() || exceedsThreshold(nodeBox)) continue;
    bbox.union(nodeBox);
    hasBounds = true;
  }
  if (!hasBounds) return "no-bounds";

  const center = bbox.getCenter(new Vector3());
  const radius = Math.max(0.25, bbox.getSize(new Vector3()).length() * 0.6);
  Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).focus(center, radius);
  return "focused";
}

function exceedsThreshold(bbox: Box3): boolean {
  const exceeds = (v: Vector3) =>
    Math.abs(v.x) > K_POLLUTION_THRESHOLD
    || Math.abs(v.y) > K_POLLUTION_THRESHOLD
    || Math.abs(v.z) > K_POLLUTION_THRESHOLD;
  return exceeds(bbox.min) || exceeds(bbox.max);
}
